import React, {useEffect} from 'react';
import {FormattedMessage} from 'react-intl';
import classNames from 'classnames';
import DataItemSelect from '../../_common/components/DataItemSelect';
import DataItem from '../../_common/components/DataItem';
import DataItemTextarea from '../../_common/components/DataItemTextarea';
import DataItemRadio from '../../_common/components/DataItemRadio';
import {floatValueSign} from '../../../../utils';
import {transferEditSelectStyles} from '../../_common/styles/reactSelectStyles';
import {monthsOptions, splitsOptions} from '../../_common/constants/options';
import styles from './styles.module.scss';

const Parameters = ({formik, getYearsOptions, newPaycheck, viewMode = false}) => {
  const prefix = 'transfersByAgent-popup-edit';
  const commonInputProps = {
    sideLabelStyle: styles.fieldLabel,
    onChange: floatValueSign,
    containerStyle: styles.field,
    dir: 'ltr',
    customInputStyle: classNames(styles.fieldInput, 'text-left'),
    disabled: viewMode
  };
  const commonSelectProps = {
    labelStyle: styles.fieldLabel,
    containerStyle: styles.field,
    customStyles: transferEditSelectStyles,
    inputContainerStyle: 'w-120px',
    disabled: viewMode
  };
  const commonRadioProps = {
    customBoxStyle: 'transfers-checkbox-size',
    customInputStyle: 'transfers-checkbox',
    customContainerStyle: 'd-flex align-items-center',
    disabled: viewMode
  };
  const radioContainerClassnames = 'w-250px d-flex align-items-center';

  const {values, setFieldValue} = formik;
  const {employerEducationFund, employerMandatoryPension, employeeEducationFund, employeeMandatoryPension} = values;

  useEffect(() => {
    if (
      parseFloat(employerEducationFund) ||
      parseFloat(employerMandatoryPension) ||
      parseFloat(employeeEducationFund) ||
      parseFloat(employeeMandatoryPension)
    ) {
      setFieldValue('pensionManagementCommission', 10);
    } else {
      setFieldValue('pensionManagementCommission', 0);
    }
  }, [employerEducationFund, employerMandatoryPension, employeeEducationFund, employeeMandatoryPension]);

  return (
    <div className={styles.flexRow}>
      <div className={styles.box}>
        <div>
          <h6 className={styles.boxSubheader}>
            <FormattedMessage id={`${prefix}-sub-header-paycheck`} defaultMessage={'Paycheck'}/>
          </h6>
          <div className={'p-2'}>
            <DataItemSelect
              fieldId={'year'}
              options={getYearsOptions()}
              label={<FormattedMessage id={`${prefix}-year`} defaultMessage="Year"/>}
              {...commonSelectProps}
            />
            <DataItemSelect
              fieldId={'month'}
              options={monthsOptions}
              label={<FormattedMessage id={`${prefix}-month`} defaultMessage="Month"/>}
              {...commonSelectProps}
            />
            <DataItemSelect
              fieldId={'splits'}
              options={splitsOptions}
              label={<FormattedMessage id={`${prefix}-splits`} defaultMessage="Splits"/>}
              {...commonSelectProps}
            />
            <DataItem
              fieldId={'advancePayment'}
              sideLabel={<FormattedMessage id={`${prefix}-advance-payment`} defaultMessage="Advance payment"/>}
              value={values.advancePayment}
              {...commonInputProps}
            />
          </div>
        </div>
        <div>
          <h6 className={styles.boxSubheader}>
            <FormattedMessage id={`${prefix}-sub-header-social`} defaultMessage={'Social'}/>
          </h6>
          <div className={'p-2'}>
            <DataItem
              fieldId={'nationalInsurance'}
              sideLabel={<FormattedMessage id={`${prefix}-insurance`} defaultMessage="National insurance"/>}
              value={values.nationalInsurance}
              disabled={!newPaycheck}
              {...commonInputProps}
            />
            <DataItem
              fieldId={'nationalInsuranceDebt'}
              sideLabel={<FormattedMessage id={`${prefix}-insurance-debt`} defaultMessage="Insurance debt"/>}
              value={values.nationalInsuranceDebt}
              {...commonInputProps}
            />
            <DataItem
              fieldId={'vat'}
              sideLabel={<FormattedMessage id={`${prefix}-vat`} defaultMessage="VAT"/>}
              value={values.vat}
              disabled={true}
              {...commonInputProps}
            />
            <DataItem
              fieldId={'incomeTaxOffset'}
              sideLabel={<FormattedMessage id={`${prefix}-income-tax`} defaultMessage="Income tax"/>}
              value={values.incomeTaxOffset}
              {...commonInputProps}
            />
          </div>
        </div>
        <div>
          <h6 className={styles.boxSubheader}>
            <FormattedMessage id={`${prefix}-sub-header-fees`} defaultMessage={'Social'}/>
          </h6>
          <div className={'p-2'}>
            <DataItem
              fieldId={'commission'}
              sideLabel={
                <div className={radioContainerClassnames}>
                  <span className={styles.neg}>*</span>
                  <FormattedMessage id={`${prefix}-commission`} defaultMessage="Commission"/>
                  <div className={'d-flex ml-auto'}>
                    <DataItemRadio
                      fieldName={'commissionValueType'}
                      id={'commission-amount'}
                      checked={values.commissionValueType === 'commission-amount'}
                      onChange={() => setFieldValue('commissionValueType', 'commission-amount')}
                      label={<FormattedMessage id={`${prefix}-amount`} defaultMessage="Amount"/>}
                      {...commonRadioProps}
                    />
                    <div className={'mx-2'}/>
                    <DataItemRadio
                      fieldName={'commissionValueType'}
                      id={'commission-percent'}
                      checked={values.commissionValueType === 'commission-percent'}
                      onChange={() => setFieldValue('commissionValueType', 'commission-percent')}
                      label={<FormattedMessage id={`${prefix}-percent`} defaultMessage="Percent"/>}
                      {...commonRadioProps}
                    />
                  </div>
                </div>
              }
              value={values.commission}
              {...commonInputProps}
            />
            <DataItem
              fieldId={'creditCardCommission'}
              sideLabel={
                <div className={radioContainerClassnames}>
                  <FormattedMessage id={`${prefix}-credit-card-commission`} defaultMessage="Credit card commission"/>
                  <div className={'d-flex ml-auto'}>
                    <DataItemRadio
                      fieldName={'creditCardCommissionValueType'}
                      id={'creditCardCommission-amount'}
                      checked={values.creditCardCommissionValueType === 'creditCardCommission-amount'}
                      onChange={() => setFieldValue('creditCardCommissionValueType', 'creditCardCommission-amount')}
                      label={<FormattedMessage id={`${prefix}-amount`} defaultMessage="Amount"/>}
                      {...commonRadioProps}
                    />
                    <div className={'mx-2'}/>
                    <DataItemRadio
                      fieldName={'creditCardCommissionValueType'}
                      id={'creditCardCommission-percent'}
                      checked={values.creditCardCommissionValueType === 'creditCardCommission-percent'}
                      onChange={() => setFieldValue('creditCardCommissionValueType', 'creditCardCommission-percent')}
                      label={<FormattedMessage id={`${prefix}-percent`} defaultMessage="Percent"/>}
                      {...commonRadioProps}
                    />
                  </div>
                </div>
              }
              value={values.creditCardCommission}
              {...commonInputProps}
            />
            <DataItem
              fieldId={'exchangeFee'}
              sideLabel={<FormattedMessage id={`${prefix}-exchange-fee`} defaultMessage="Exchange fee"/>}
              value={values.exchangeFee}
              {...commonInputProps}
            />
            <DataItem
              fieldId={'additionalCommission'}
              sideLabel={<FormattedMessage id={`${prefix}-additionalCommission`} defaultMessage="Additional commission"/>}
              value={values.additionalCommission}
              {...commonInputProps}
            />
            <DataItem
              fieldId={'pensionManagementCommission'}
              sideLabel={<FormattedMessage id={`${prefix}-pension`} defaultMessage="Pension"/>}
              value={values.pensionManagementCommission}
              disabled={!newPaycheck}
              {...commonInputProps}
            />
          </div>
        </div>
      </div>
      <div className={styles.box}>
        <h6 className={styles.boxSubheader}>
          <FormattedMessage id={`${prefix}-sub-header-pension-benefit`} defaultMessage={'Pension and benefit costs'}/>
        </h6>
        <div className={'p-2'}>
          <DataItem
            fieldId={'employerEducationFund'}
            sideLabel={
              <div className={radioContainerClassnames}>
                <FormattedMessage id={`${prefix}-education-fund-employer`} defaultMessage="Education fund (employer)"/>
                <div className={'d-flex ml-auto'}>
                  <DataItemRadio
                    fieldName={'employerEducationFundValueType'}
                    id={'employerEducationFund-amount'}
                    checked={values.employerEducationFundValueType === 'employerEducationFund-amount'}
                    onChange={() => setFieldValue('employerEducationFundValueType', 'employerEducationFund-amount')}
                    label={<FormattedMessage id={`${prefix}-amount`} defaultMessage="Amount"/>}
                    {...commonRadioProps}
                  />
                  <div className={'mx-2'}/>
                  <DataItemRadio
                    fieldName={'employerEducationFundValueType'}
                    id={'employerEducationFund-percent'}
                    checked={values.employerEducationFundValueType === 'employerEducationFund-percent'}
                    onChange={() => {
                      setFieldValue('employerEducationFundValueType', 'employerEducationFund-percent');
                      setFieldValue('employerEducationFund', '7.5');
                    }}
                    label={<FormattedMessage id={`${prefix}-percent`} defaultMessage="Percent"/>}
                    {...commonRadioProps}
                  />
                </div>
              </div>
            }
            value={values.employerEducationFund}
            {...commonInputProps}
          />
          <DataItem
            fieldId={'employerProvidentFund'}
            sideLabel={
              <div className={radioContainerClassnames}>
                <FormattedMessage id={`${prefix}-provident-fund-employer`} defaultMessage="Provident fund (employer)"/>
                <div className={'d-flex ml-auto'}>
                  <DataItemRadio
                    fieldName={'employerProvidentFundValueType'}
                    id={'employerProvidentFund-amount'}
                    checked={values.employerProvidentFundValueType === 'employerProvidentFund-amount'}
                    onChange={() => setFieldValue('employerProvidentFundValueType', 'employerProvidentFund-amount')}
                    label={<FormattedMessage id={`${prefix}-amount`} defaultMessage="Amount"/>}
                    {...commonRadioProps}
                  />
                  <div className={'mx-2'}/>
                  <DataItemRadio
                    fieldName={'employerProvidentFundValueType'}
                    id={'employerProvidentFund-percent'}
                    checked={values.employerProvidentFundValueType === 'employerProvidentFund-percent'}
                    onChange={() => setFieldValue('employerProvidentFundValueType', 'employerProvidentFund-percent')}
                    label={<FormattedMessage id={`${prefix}-percent`} defaultMessage="Percent"/>}
                    {...commonRadioProps}
                  />
                </div>
              </div>
            }
            value={values.employerProvidentFund}
            {...commonInputProps}
          />
          <DataItem
            fieldId={'employerMandatoryPension'}
            sideLabel={
              <div className={radioContainerClassnames}>
                <FormattedMessage id={`${prefix}-mandatory-pension-employer`} defaultMessage="Mandatory pension (employer)"/>
                <div className={'d-flex ml-auto'}>
                  <DataItemRadio
                    fieldName={'employerMandatoryPensionValueType'}
                    id={'employerMandatoryPension-amount'}
                    checked={values.employerMandatoryPensionValueType === 'employerMandatoryPension-amount'}
                    onChange={() => setFieldValue('employerMandatoryPensionValueType', 'employerMandatoryPension-amount')}
                    label={<FormattedMessage id={`${prefix}-amount`} defaultMessage="Amount"/>}
                    {...commonRadioProps}
                  />
                  <div className={'mx-2'}/>
                  <DataItemRadio
                    fieldName={'employerMandatoryPensionValueType'}
                    id={'employerMandatoryPension-percent'}
                    checked={values.employerMandatoryPensionValueType === 'employerMandatoryPension-percent'}
                    onChange={() => {
                      setFieldValue('employerMandatoryPensionValueType', 'employerMandatoryPension-percent');
                      setFieldValue('employerMandatoryPension', '12.5');
                    }}
                    label={<FormattedMessage id={`${prefix}-percent`} defaultMessage="Percent"/>}
                    {...commonRadioProps}
                  />
                </div>
              </div>
            }
            value={values.employerMandatoryPension}
            {...commonInputProps}
          />
          <DataItem
            fieldId={'compensations'}
            sideLabel={
              <div className={radioContainerClassnames}>
                <FormattedMessage id={`${prefix}-compensations`} defaultMessage="Compensations"/>
                <div className={'d-flex ml-auto'}>
                  <DataItemRadio
                    fieldName={'compensationsValueType'}
                    id={'compensations-amount'}
                    checked={values.compensationsValueType === 'compensations-amount'}
                    onChange={() => setFieldValue('compensationsValueType', 'compensations-amount')}
                    label={<FormattedMessage id={`${prefix}-amount`} defaultMessage="Amount"/>}
                    {...commonRadioProps}
                  />
                  <div className={'mx-2'}/>
                  <DataItemRadio
                    fieldName={'compensationsValueType'}
                    id={'compensations-percent'}
                    checked={values.compensationsValueType === 'compensations-percent'}
                    onChange={() => setFieldValue('compensationsValueType', 'compensations-percent')}
                    label={<FormattedMessage id={`${prefix}-percent`} defaultMessage="Percent"/>}
                    {...commonRadioProps}
                  />
                </div>
              </div>
            }
            value={values.compensations}
            {...commonInputProps}
          />
          <h6 className={styles.boxSubheader}/>
          <DataItem
            fieldId={'employeeEducationFund'}
            sideLabel={
              <div className={radioContainerClassnames}>
                <FormattedMessage id={`${prefix}-education-fund-employee`} defaultMessage="Education fund (employee)"/>
                <div className={'d-flex ml-auto'}>
                  <DataItemRadio
                    fieldName={'employeeEducationFundValueType'}
                    id={'employeeEducationFund-amount'}
                    checked={values.employeeEducationFundValueType === 'employeeEducationFund-amount'}
                    onChange={() => setFieldValue('employeeEducationFundValueType', 'employeeEducationFund-amount')}
                    label={<FormattedMessage id={`${prefix}-amount`} defaultMessage="Amount"/>}
                    {...commonRadioProps}
                  />
                  <div className={'mx-2'}/>
                  <DataItemRadio
                    fieldName={'employeeEducationFundValueType'}
                    id={'employeeEducationFund-percent'}
                    checked={values.employeeEducationFundValueType === 'employeeEducationFund-percent'}
                    onChange={() => {
                      setFieldValue('employeeEducationFundValueType', 'employeeEducationFund-percent');
                      setFieldValue('employeeEducationFund', '2.5');
                    }}
                    label={<FormattedMessage id={`${prefix}-percent`} defaultMessage="Percent"/>}
                    {...commonRadioProps}
                  />
                </div>
              </div>
            }
            value={values.employeeEducationFund}
            {...commonInputProps}
          />
          <DataItem
            fieldId={'employeeProvidentFund'}
            sideLabel={
              <div className={radioContainerClassnames}>
                <FormattedMessage id={`${prefix}-provident-fund-employee`} defaultMessage="Provident fund (employee)"/>
                <div className={'d-flex ml-auto'}>
                  <DataItemRadio
                    fieldName={'employeeProvidentFundValueType'}
                    id={'employeeProvidentFund-amount'}
                    checked={values.employeeProvidentFundValueType === 'employeeProvidentFund-amount'}
                    onChange={() => setFieldValue('employeeProvidentFundValueType', 'employeeProvidentFund-amount')}
                    label={<FormattedMessage id={`${prefix}-amount`} defaultMessage="Amount"/>}
                    {...commonRadioProps}
                  />
                  <div className={'mx-2'}/>
                  <DataItemRadio
                    fieldName={'employeeProvidentFundValueType'}
                    id={'employeeProvidentFund-percent'}
                    checked={values.employeeProvidentFundValueType === 'employeeProvidentFund-percent'}
                    onChange={() => setFieldValue('employeeProvidentFundValueType', 'employeeProvidentFund-percent')}
                    label={<FormattedMessage id={`${prefix}-percent`} defaultMessage="Percent"/>}
                    {...commonRadioProps}
                  />
                </div>
              </div>
            }
            value={values.employeeProvidentFund}
            {...commonInputProps}
          />
          <DataItem
            fieldId={'employeeMandatoryPension'}
            sideLabel={
              <div className={radioContainerClassnames}>
                <FormattedMessage id={`${prefix}-mandatory-pension-employee`} defaultMessage="Mandatory pension (employee)"/>
                <div className={'d-flex ml-auto'}>
                  <DataItemRadio
                    fieldName={'employeeMandatoryPensionValueType'}
                    id={'employeeMandatoryPension-amount'}
                    checked={values.employeeMandatoryPensionValueType === 'employeeMandatoryPension-amount'}
                    onChange={() => setFieldValue('employeeMandatoryPensionValueType', 'employeeMandatoryPension-amount')}
                    label={<FormattedMessage id={`${prefix}-amount`} defaultMessage="Amount"/>}
                    {...commonRadioProps}
                  />
                  <div className={'mx-2'}/>
                  <DataItemRadio
                    fieldName={'employeeMandatoryPensionValueType'}
                    id={'employeeMandatoryPension-percent'}
                    checked={values.employeeMandatoryPensionValueType === 'employeeMandatoryPension-percent'}
                    onChange={() => {
                      setFieldValue('employeeMandatoryPensionValueType', 'employeeMandatoryPension-percent');
                      setFieldValue('employeeMandatoryPension', '6');
                    }}
                    label={<FormattedMessage id={`${prefix}-percent`} defaultMessage="Percent"/>}
                    {...commonRadioProps}
                  />
                </div>
              </div>
            }
            value={values.employeeMandatoryPension}
            {...commonInputProps}
          />
        </div>
        <div>
          <h6 className={styles.boxSubheader}>
            <FormattedMessage id={`${prefix}-sub-header-expenses`} defaultMessage={'Expenses'}/>
          </h6>
          <div className={'p-2'}>
            <DataItem
              fieldId={'expensesWithVat'}
              sideLabel={<FormattedMessage id={`${prefix}-expenses-with-VAT`} defaultMessage="Expenses with VAT"/>}
              value={values.expensesWithVat}
              {...commonInputProps}
            />
            <DataItem
              fieldId={'expensesWithoutVat'}
              sideLabel={<FormattedMessage id={`${prefix}-expenses-without-VAT`}
                                           defaultMessage="Expenses without VAT"/>}
              value={values.expensesWithoutVat}
              {...commonInputProps}
            />
            <DataItem
              fieldId={'supplierExpensesRefund'}
              sideLabel={<FormattedMessage id={`${prefix}-supplier-expenses-refund`}
                                           defaultMessage="Supplier expenses refund"/>}
              value={values.supplierExpensesRefund}
              {...commonInputProps}
            />
          </div>
          <div>
            <h6 className={styles.boxSubheader}>
              <FormattedMessage id={`${prefix}-sub-header-adminNotes`} defaultMessage={'Social'}/>
            </h6>
            <DataItemTextarea
              fieldId={'adminNotes'}
              placeholder={'הערות מנהל להעברה זו בלבד'}
              value={values.adminNotes}
              customTextareaStyle={styles.textarea}
              minRows={3}
              maxRows={3}
              disabled={viewMode}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Parameters;